'use client';

import Image from 'next/image';
import Link from 'next/link';
import { memo, useState } from 'react';
import type { ServiceCardProps } from '@routeroyal/theme-core';

export const RouteRoyalServiceCard = memo(function RouteRoyalServiceCard({ service }: ServiceCardProps) {
  const [imageError, setImageError] = useState(false);

  const cardContent = (
    <>
      {service.icon && (
        <div className="relative h-48 bg-gradient-to-br from-blue-50 to-gray-50">
          {!imageError ? (
            <Image
              src={service.icon}
              alt={service.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-blue-50">
              <svg className="w-12 h-12 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
            </div>
          )}
        </div>
      )}
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-700 transition-colors">
          {service.title}
        </h3>
        <p className="text-gray-600 text-sm leading-relaxed mb-4">
          {service.description}
        </p>
        {service.link && (
          <span className="inline-flex items-center text-sm font-medium text-blue-700 group-hover:text-blue-800 transition-colors">
            Learn more
            <svg className="ml-1.5 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </span>
        )}
      </div>
    </>
  );

  if (service.link) {
    return (
      <Link href={service.link} className="group block">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-blue-200 transition-all duration-300">
          {cardContent}
        </div>
      </Link>
    );
  }

  return (
    <div className="group bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg hover:border-blue-200 transition-all duration-300">
      {cardContent}
    </div>
  );
});
